import { useApp } from '@/lib/core/providers/appProvider';
import { ImageWithCrops } from '@/services/platform/api/model';
import { formatDate, formatTime, getFirstName, ImageMap, timePartIconFromTime } from '@/lib/core/utils';
import type { ConfirmationBookProps } from '../types';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core';
import { ConfirmationBanner } from '@/components/shared/booking/common/confirmation/banner';
import { useEffect } from 'react';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { Icon } from '@/components/ui/icon';
import { useBookingShare } from '@/lib/core/utils/share';

export function ConfirmationBook({
  booking,
  reservation,
  cmsProps,
  onGoHome,
  onSeeBookingDetails,
}: ConfirmationBookProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(s => s.locale);
  const { user } = useApp() as { user?: { name?: string | null } };
  const setMainClassName = useUIStore(s => s.setMainClassName);
  const { handleShare } = useBookingShare(reservation);

  useEffect(() => {
    setMainClassName('pb-0!');
    return () => setMainClassName('');
  }, [setMainClassName]);

  const firstName = getFirstName(user?.name) ?? null;

  const venueImage = ImageMap(booking.venue?.image as ImageWithCrops, 'bookingDetailHero');
  const venueLogo = ImageMap(booking.venue?.logo as ImageWithCrops);
  const venueDisplay = booking.venue?.name ?? '';
  const guestDisplay =
    booking.guestCount !== null ? `${booking.guestCount} ${t('App.Labels.ParkingSpaces')}` : '';

  const dateDisplay = booking.date ? formatDate(booking.date?.toDateString(), locale) : '';
  const timeDisplay = booking.time ? formatTime(booking.time) : '';
  const timeIcon = timePartIconFromTime(booking.time);

  return (
    <ConfirmationBanner
      image={venueImage}
      logo={venueLogo}
      firstName={firstName}
      title={cmsProps?.title ?? t('App.Labels.BookingConfirmedText')}
      rows={[
        [
          ...(dateDisplay ? [{ text: dateDisplay }] : []),
          ...(timeDisplay ? [{ icon: timeIcon, text: timeDisplay }] : []),
        ],
        booking.service
          ? [...(booking.service.name ? [{ icon: 'spark', text: booking.service.name ?? '' }] : [])]
          : [],
        [
          ...(venueDisplay ? [{ icon: 'building', text: venueDisplay }] : []),
          ...(guestDisplay ? [{ icon: 'parking', text: guestDisplay }] : []),
        ],
      ]}
      topPanel={
        <div className="pt-safe-area flex items-center justify-end px-4">
          <button
            onClick={handleShare}
            className="flex size-10 items-center justify-center"
            aria-label={t('App.Labels.ShareGuests')}
          >
            <Icon name="ShareIos" width={24} height={24} strokeWidth={1.5} className="text-white" />
          </button>
        </div>
      }
      actions={[
        {
          label: cmsProps?.goBackLabel ?? t('App.Labels.GoBackHome'),
          onClick: onGoHome,
          variant: 'secondary-outline',
        },
        {
          label: cmsProps?.seeBookingDetailsLabel ?? t('App.Labels.SeeBookingDetails'),
          onClick: onSeeBookingDetails,
          variant: 'primary-solid',
        },
      ]}
      className="-mt-[max(1rem,env(safe-area-inset-top))]!"
    />
  );
}
